import { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Database } from '../../../database.types'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { Label } from "@/components/ui/label"

type NewsSource = Database['public']['Tables']['custom_news_sources']['Row']

export default function NewsSourcesManagement() {
    const [sources, setSources] = useState<NewsSource[]>([])
    const [isLoading, setIsLoading] = useState(true)
    console.log(isLoading); // fix linter
    const [isEditing, setIsEditing] = useState<string | null>(null)
    const { toast } = useToast()

    // Form state
    const [formData, setFormData] = useState<Partial<NewsSource>>({
        source_name: '',
        source_type: 'url',
        source_description: '',
        news_source_url: '',
        rss_url: '',
        email_inbox_address: '',
        email_imap_server: '',
        email_imap_port: 993,
        email_imap_username: '',
        email_imap_password: '',
        ingestion_filtering_rules: '',
    })

    useEffect(() => {
        fetchSources()
    }, [])

    const fetchSources = async () => {
        try {
            const { data, error } = await supabase
                .from('custom_news_sources')
                .select('*')
                .order('source_name')

            if (error) throw error
            setSources(data || [])
        } catch (error) {
            console.error('Error fetching sources:', error)
            toast({
                title: "Error",
                description: "Failed to fetch news sources",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            if (isEditing) {
                const { error } = await supabase
                    .from('custom_news_sources')
                    .update(formData)
                    .eq('id', isEditing)

                if (error) throw error

                toast({
                    title: "Success",
                    description: "News source updated successfully",
                })
            } else {
                const now = new Date().toISOString()
                const { error } = await supabase
                    .from('custom_news_sources')
                    .insert([{
                        ...formData,
                        created_at: now,
                        updated_at: now,
                        source_name: formData.source_name!,
                        source_type: formData.source_type!
                    }])

                if (error) throw error

                toast({
                    title: "Success",
                    description: "News source added successfully",
                })
            }

            setIsEditing(null)
            setFormData({
                source_name: '',
                source_type: 'url',
                source_description: '',
                news_source_url: '',
                rss_url: '',
                email_inbox_address: '',
                email_imap_server: '',
                email_imap_port: 993,
                email_imap_username: '',
                email_imap_password: '',
                ingestion_filtering_rules: '',
            })
            fetchSources()
        } catch (error) {
            console.error('Error saving source:', error)
            toast({
                title: "Error",
                description: "Failed to save news source",
                variant: "destructive",
            })
        }
    }

    const handleEdit = (source: NewsSource) => {
        setIsEditing(source.id)
        setFormData(source)
    }

    const handleDelete = async (id: string) => {
        try {
            const { error } = await supabase
                .from('custom_news_sources')
                .delete()
                .eq('id', id)

            if (error) throw error

            toast({
                title: "Success",
                description: "News source deleted successfully",
            })
            fetchSources()
        } catch (error) {
            console.error('Error deleting source:', error)
            toast({
                title: "Error",
                description: "Failed to delete news source",
                variant: "destructive",
            })
        }
    }

    return (
        <div className="space-y-4">
            <Dialog>
                <DialogTrigger asChild>
                    <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add News Source
                    </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{isEditing ? 'Edit' : 'Add'} News Source</DialogTitle>
                        <DialogDescription>
                            Configure a new source for news content ingestion.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="source_name">Source Name</Label>
                            <Input
                                id="source_name"
                                value={formData.source_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="source_type">Source Type</Label>
                            <Select
                                value={formData.source_type}
                                onValueChange={(value: Database['public']['Enums']['enum_custom_news_sources_source_type']) =>
                                    setFormData(prev => ({ ...prev, source_type: value }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select source type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="url">URL</SelectItem>
                                    <SelectItem value="rss">RSS</SelectItem>
                                    <SelectItem value="email">Email</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="source_description">Description</Label>
                            <Input
                                id="source_description"
                                value={formData.source_description || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, source_description: e.target.value }))}
                            />
                        </div>

                        {formData.source_type === 'url' && (
                            <div className="space-y-2">
                                <Label htmlFor="news_source_url">Source URL</Label>
                                <Input
                                    id="news_source_url"
                                    value={formData.news_source_url || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, news_source_url: e.target.value }))}
                                />
                            </div>
                        )}

                        {formData.source_type === 'rss' && (
                            <div className="space-y-2">
                                <Label htmlFor="rss_url">RSS URL</Label>
                                <Input
                                    id="rss_url"
                                    value={formData.rss_url || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, rss_url: e.target.value }))}
                                />
                            </div>
                        )}

                        {formData.source_type === 'email' && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="email_inbox_address">Email Address</Label>
                                    <Input
                                        id="email_inbox_address"
                                        value={formData.email_inbox_address || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email_inbox_address: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email_imap_server">IMAP Server</Label>
                                    <Input
                                        id="email_imap_server"
                                        value={formData.email_imap_server || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email_imap_server: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email_imap_port">IMAP Port</Label>
                                    <Input
                                        id="email_imap_port"
                                        type="number"
                                        value={formData.email_imap_port || 993}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email_imap_port: parseInt(e.target.value) }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email_imap_username">IMAP Username</Label>
                                    <Input
                                        id="email_imap_username"
                                        value={formData.email_imap_username || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email_imap_username: e.target.value }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email_imap_password">IMAP Password</Label>
                                    <Input
                                        id="email_imap_password"
                                        type="password"
                                        value={formData.email_imap_password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email_imap_password: e.target.value }))}
                                    />
                                </div>
                            </>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="ingestion_filtering_rules">Filtering Rules</Label>
                            <Input
                                id="ingestion_filtering_rules"
                                value={formData.ingestion_filtering_rules || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, ingestion_filtering_rules: e.target.value }))}
                            />
                        </div>

                        <DialogFooter>
                            <Button type="submit">{isEditing ? 'Update' : 'Add'} Source</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sources.map((source) => (
                            <TableRow key={source.id}>
                                <TableCell className="font-medium">{source.source_name}</TableCell>
                                <TableCell>{source.source_type}</TableCell>
                                <TableCell>{source.source_description}</TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEdit(source)}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(source.id)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
} 